import { AppTheme, Organization, SCBrand } from '@ngb-frontend/shared/types'

import {
  LEASEPLAN_REMOTE,
  defaultBusinessRules,
  type BusinessRules,
  type BusinessRulesOverride,
  type LocaleSettings,
} from './defaults'
import { HOLIDAYS } from './holidays'

import type { QueryCountryCode } from '@ngb-frontend/shared/types'

// ── Shared fragments ─────────────────────────────────────────────────────────
// Extracted so sibling countries stay in lock-step instead of drifting.

/** The Ayvens "Default" business-rules superset (MOT-exclusive + supplier autopick) */
const AYVENS_DEFAULT_RULESET = {
  services: { isDefaultRuleset: true },
  brand: { autopickSupplier: true },
} satisfies BusinessRulesOverride

/** France — one locale shared by all three subsidiary codes */
const FR_LOCALE = {
  country: 'fr',
  tz: 'Europe/Paris',
  locales: ['fr-FR', 'en-FR'],
  defaultLocale: 'fr-FR',
} satisfies LocaleSettings

/** Both Swedish subsidiaries share everything except brand */
const SE_SHARED = {
  locale: {
    country: 'se',
    tz: 'Europe/Stockholm',
    locales: ['sv-SE', 'en-SE'],
    defaultLocale: 'sv-SE',
  },
  dates: {
    holidays: HOLIDAYS.sweden,
    eolBookingHorizonEnd: '2025-08-22',
    operatingHours: {
      maintenanceAndRepair: [
        {
          type: 'RegularMaintenance',
          time: { minTime: '07:00', maxTime: '09:00' },
        },
        { type: 'Repair', time: { minTime: '07:00', maxTime: '09:00' } },
      ],
    },
  },
  services: { isDefaultRuleset: true, motExtraServicePolicy: 'sweden' },
} satisfies BusinessRulesOverride

/**
 * THE country registry — one block per country, every static customization
 * across every domain (org, locale, dates, services, brand, and the
 * Salesforce-owned `remote` shape). Each entry lists ONLY what differs from
 * {@link defaultBusinessRules}; GLOBAL is absent (it IS the defaults). Bulky
 * holiday calendars live in the collocated `holidays.ts`.
 *
 * Onboarding a country = add one block here (+ its calendar if any). The
 * `satisfies Record<Exclude<QueryCountryCode,'GLOBAL'>, ...>` guard makes a
 * newly-added country code a compile error until it has a block — so a country
 * can never be silently half-configured.
 */
export const BUSINESS_RULES: Partial<
  Record<QueryCountryCode, BusinessRulesOverride>
> = {
  // ── Live countries ─────────────────────────────────────────────────────────
  LPUK: {
    org: Organization.LP,
    locale: {
      country: 'gb',
      tz: 'Europe/London',
      locales: ['en-GB'],
      defaultLocale: 'en-GB',
    },
    dates: { eolBookingHorizonEnd: '2025-08-22' },
    remote: LEASEPLAN_REMOTE,
  },

  '4001': {
    // Austria
    locale: {
      country: 'at',
      tz: 'Europe/Vienna',
      locales: ['de-AT', 'en-AT'],
      defaultLocale: 'de-AT',
    },
    dates: {
      holidays: HOLIDAYS.austria,
      operatingHours: {
        any: [
          {
            type: 'PickupAndReturn',
            time: {
              options: [
                { value: '08:00', label: '08:00 - 09:00' },
                { value: '09:00', label: '09:00 - 10:00' },
              ],
            },
          },
          {
            type: 'RegularMaintenance',
            time: { minTime: '08:00', maxTime: '13:00' },
          },
          { type: 'Repair', time: { minTime: '08:00', maxTime: '13:00' } },
          { type: 'MOT', time: { minTime: '08:00', maxTime: '17:00' } },
        ],
      },
      // Austrian suppliers offer morning slots, so the user keeps the time choice
      pickup: { userSelectsTime: true },
    },
    services: { motExtraServicePolicy: 'austriaSwitzerland' },
  },

  '10001': {
    // Bulgaria
    locale: {
      country: 'bg',
      tz: 'Europe/Sofia',
      locales: ['bg-BG', 'en-BG'],
      defaultLocale: 'bg-BG',
    },
    dates: {
      operatingHours: {
        any: [
          {
            type: 'PickupAndReturn',
            time: { minTime: '08:00', maxTime: '17:00' },
          },
          {
            type: 'RegularMaintenance',
            time: { minTime: '08:00', maxTime: '17:00' },
          },
          { type: 'Repair', time: { minTime: '08:00', maxTime: '17:00' } },
          { type: 'MOT', time: { minTime: '08:00', maxTime: '17:00' } },
        ],
      },
      pickup: { userSelectsTime: true },
    },
    services: { motExtraServicePolicy: 'none', mainServicesExclusive: true },
    brand: {
      showDriverChangeWarning: false,
      showPickupAddressInSummary: false,
    },
  },

  '20301': {
    // Czechia
    locale: {
      country: 'cz',
      tz: 'Europe/Prague',
      locales: ['cs-CZ', 'en-CZ'],
      defaultLocale: 'cs-CZ',
    },
    ...AYVENS_DEFAULT_RULESET,
  },

  '24601': {
    // Finland — Default ruleset, but Repair is not offered
    locale: {
      country: 'fi',
      tz: 'Europe/Helsinki',
      locales: ['fi-FI', 'en-FI'],
      defaultLocale: 'fi-FI',
    },
    services: { isDefaultRuleset: true, repairOffered: false },
    brand: { autopickSupplier: true },
  },

  '44201': {
    // Luxembourg
    locale: {
      country: 'lu',
      tz: 'Europe/Luxembourg',
      locales: ['fr-LU', 'de-LU', 'en-LU'],
      defaultLocale: 'fr-LU',
    },
    ...AYVENS_DEFAULT_RULESET,
  },

  '57801': {
    // Norway
    locale: {
      country: 'no',
      tz: 'Europe/Oslo',
      locales: ['en-NO', 'nb-NO'],
      defaultLocale: 'nb-NO',
    },
    dates: { holidays: HOLIDAYS.norway },
  },

  '64201': {
    // Romania
    locale: {
      country: 'ro',
      tz: 'Europe/Bucharest',
      locales: ['ro-RO', 'en-RO'],
      defaultLocale: 'ro-RO',
    },
    dates: { holidays: HOLIDAYS.romania },
    services: { motExtraServicePolicy: 'romania' },
    brand: { phoneNumber: '+40-021-301-49-59' },
  },

  '68801': {
    // Serbia
    locale: {
      country: 'rs',
      tz: 'Europe/Belgrade',
      locales: ['sr-RS', 'en-RS'],
      defaultLocale: 'sr-RS',
    },
    ...AYVENS_DEFAULT_RULESET,
  },

  '75201': {
    // Sweden — Ayvens
    ...SE_SHARED,
    brand: { phoneNumber: '+46-8-501-122-00', autopickSupplier: true },
  },

  '75202': {
    // Sweden — Nordea / NF Fleet (own theme, Sitecore brand, phone)
    ...SE_SHARED,
    brand: {
      theme: AppTheme.Nordea,
      sitecoreBrand: SCBrand.NFFleet,
      phoneNumber: '+46-8-501-123-60',
      autopickSupplier: true,
    },
  },

  '75601': {
    // Switzerland
    locale: {
      country: 'ch',
      tz: 'Europe/Zurich',
      locales: ['fr-CH', 'de-CH', 'it-CH', 'en-CH'],
      defaultLocale: 'de-CH',
    },
    dates: {
      // PickupAndReturn morning slots mirror Austria (AT/CH share them)
      operatingHours: {
        any: [
          {
            type: 'PickupAndReturn',
            time: {
              options: [
                { value: '08:00', label: '08:00 - 09:00' },
                { value: '09:00', label: '09:00 - 10:00' },
              ],
            },
          },
          {
            type: 'RegularMaintenance',
            time: { minTime: '08:00', maxTime: '13:00' },
          },
          { type: 'Repair', time: { minTime: '08:00', maxTime: '13:00' } },
          { type: 'MOT', time: { minTime: '08:00', maxTime: '17:00' } },
        ],
      },
      // Swiss suppliers offer morning slots, so the user keeps the time choice
      pickup: { userSelectsTime: true },
    },
    services: { motExtraServicePolicy: 'austriaSwitzerland' },
    brand: { phoneNumber: '+41-746-63-33' },
  },

  // ── Planned — NOT yet live ──────────────────────────────────────────────────
  // Locale/org carried over from the pre-registry config; no active journey yet
  // and not on the roadmap. Present so the exhaustiveness guard stays total and
  // onboarding one is only a matter of filling in the other domains.
  '5601': {
    // Belgium
    locale: {
      country: 'be',
      tz: 'Europe/Brussels',
      locales: ['nl-BE', 'en-BE'],
      defaultLocale: 'en-BE',
    },
  },
  '25001': { locale: FR_LOCALE }, // France
  '25002': { locale: FR_LOCALE }, // France
  '25003': { locale: FR_LOCALE }, // France
  '48401': {
    // Mexico
    locale: {
      country: 'mx',
      tz: 'America/Mexico_City',
      locales: ['es-MX', 'en-MX'],
      defaultLocale: 'es-MX',
    },
  },
} satisfies Record<Exclude<QueryCountryCode, 'GLOBAL'>, BusinessRulesOverride>

/** Merge a country's registry entry onto the defaults into its full settings. */
export const getBusinessRules = (
  countryCode?: QueryCountryCode,
): BusinessRules => {
  const o = (countryCode && BUSINESS_RULES[countryCode]) || {}
  const d = defaultBusinessRules

  return {
    org: o.org ?? d.org,
    locale: { ...d.locale, ...o.locale },
    dates: {
      ...d.dates,
      ...o.dates,
      leadDays: {
        ...d.dates.leadDays,
        ...o.dates?.leadDays,
        eol: { ...d.dates.leadDays.eol, ...o.dates?.leadDays?.eol },
      },
      operatingHours: { ...d.dates.operatingHours, ...o.dates?.operatingHours },
      pickup: { ...d.dates.pickup, ...o.dates?.pickup },
    },
    services: { ...d.services, ...o.services },
    brand: { ...d.brand, ...o.brand },
    remote: { ...d.remote, ...o.remote },
  }
}
